import {
  LayoutDashboard, Users, DollarSign, GraduationCap, Settings,
  Trophy, FlaskConical, Building2, MapPin,
  UserCog, CalendarDays, CreditCard, Users2, Wallet, Calendar,
  UserCheck, Swords, Megaphone, FileText, Receipt, BarChart3, FileCheck, Package, ShoppingCart,
  Instagram as InstagramIcon, Bell as BellIcon, Percent, RefreshCw,
  Plug, UserPlus, LifeBuoy,
} from 'lucide-react';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';

export type NavItem = {
  titleKey: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredPlan?: 'enterprise';
  /**
   * Chave de permissão ('domain.action') que controla a visibilidade deste
   * item (menu) e o guard de rota correspondente em App.tsx
   * (`<RequirePermission permission={...} module={...}>`). Ausente apenas
   * nos itens de `professorNavGroups` (nav própria, fora da matriz — ver
   * RequirePermission.tsx).
   */
  permissionKey?: string;
  /** Módulo do clube (club_modules) que precisa estar habilitado. */
  moduleKey?: string;
  subItems?: NavItem[];
};

export type NavGroup = {
  labelKey: string;
  items: NavItem[];
  requiredPlan?: 'enterprise';
};

/**
 * Navegação do clube tradicional (kind='club'). Cada item carrega
 * `permissionKey`/`moduleKey` — a fonte única para filtro de menu
 * (AppSidebar.tsx) e guard de rota (App.tsx). Substituiu o antigo
 * `ROLE_ALLOWED_ROUTES`/`teacherStaffNavGroups`: a visibilidade por papel
 * agora vem inteiramente da matriz de permissões (role_permission_defaults
 * + club_role_permissions), resolvida em usePermissions().
 */
export const navGroups: NavGroup[] = [
  {
    labelKey: 'nav.crm',
    items: [
      { titleKey: 'nav.dashboard', url: '/', icon: LayoutDashboard, permissionKey: 'dashboard.view', moduleKey: 'dashboard' },
      { titleKey: 'nav.leads', url: '/leads', icon: Users, permissionKey: 'leads.view', moduleKey: 'leads' },
      { titleKey: 'nav.opportunities', url: '/oportunidades', icon: DollarSign, requiredPlan: 'enterprise', permissionKey: 'opportunities.view', moduleKey: 'opportunities' },
      {
        titleKey: 'nav.integrations',
        url: '/agregadores',
        icon: Plug,
        requiredPlan: 'enterprise',
        permissionKey: 'integrations.view',
        moduleKey: 'integrations',
        subItems: [
          { titleKey: 'nav.aggregators', url: '/agregadores', icon: Building2, permissionKey: 'integrations.view', moduleKey: 'integrations' },
          { titleKey: 'nav.whatsapp', url: '/whatsapp', icon: WhatsAppIcon, requiredPlan: 'enterprise', permissionKey: 'integrations.view', moduleKey: 'integrations' },
          { titleKey: 'nav.instagram', url: '/instagram', icon: InstagramIcon, requiredPlan: 'enterprise', permissionKey: 'integrations.view', moduleKey: 'integrations' },
        ],
      },
    ],
  },
  {
    labelKey: 'nav.operational',
    items: [
      { titleKey: 'nav.agenda', url: '/agenda', icon: Calendar, permissionKey: 'agenda.view', moduleKey: 'agenda' },
      { titleKey: 'nav.courts', url: '/quadras', icon: MapPin, permissionKey: 'courts.view', moduleKey: 'courts' },
      { titleKey: 'nav.members', url: '/membros', icon: UserCheck, permissionKey: 'members.view', moduleKey: 'members' },
      { titleKey: 'nav.tournaments', url: '/torneios', icon: Swords, permissionKey: 'tournaments.view', moduleKey: 'tournaments' },
      { titleKey: 'nav.categories', url: '/categorias', icon: Trophy, permissionKey: 'categories.view', moduleKey: 'categories' },
      { titleKey: 'nav.indicators', url: '/indicadores', icon: BarChart3, requiredPlan: 'enterprise', permissionKey: 'indicators.view', moduleKey: 'indicators' },
    ],
  },
  {
    labelKey: 'nav.academy',
    items: [
      { titleKey: 'nav.teachers', url: '/professores', icon: UserCog, permissionKey: 'teachers.view', moduleKey: 'academy_teachers' },
      { titleKey: 'nav.students', url: '/alunos', icon: GraduationCap, permissionKey: 'students.view', moduleKey: 'academy_students' },
      { titleKey: 'nav.lessons', url: '/aulas', icon: CalendarDays, permissionKey: 'lessons.view', moduleKey: 'academy_lessons' },
      { titleKey: 'nav.makeup', url: '/reposicoes', icon: RefreshCw, permissionKey: 'makeups.view', moduleKey: 'academy_makeups' },
      { titleKey: 'nav.trialLessons', url: '/experimentais', icon: FlaskConical, permissionKey: 'trials.view', moduleKey: 'academy_trials' },
    ],
    requiredPlan: 'enterprise',
  },
  {
    labelKey: 'nav.financial',
    items: [
      { titleKey: 'nav.financialPanel', url: '/financeiro', icon: BarChart3, requiredPlan: 'enterprise', permissionKey: 'finance_panel.view', moduleKey: 'finance_panel' },
      { titleKey: 'nav.plans', url: '/planos', icon: CreditCard, requiredPlan: 'enterprise', permissionKey: 'tuition_plans.view', moduleKey: 'tuition_plans' },
      { titleKey: 'nav.invoices', url: '/faturas', icon: FileText, requiredPlan: 'enterprise', permissionKey: 'invoices.view', moduleKey: 'invoices' },
      { titleKey: 'nav.commissions', url: '/comissoes', icon: Percent, requiredPlan: 'enterprise', permissionKey: 'commissions.view', moduleKey: 'commissions' },
      { titleKey: 'nav.expenses', url: '/despesas', icon: Receipt, requiredPlan: 'enterprise', permissionKey: 'expenses.view', moduleKey: 'expenses' },
      // PDV C1/C2 (spec 2026-07-10): professor sem acesso (D5) — sem defaults na matriz.
      { titleKey: 'nav.pdv', url: '/pdv', icon: ShoppingCart, requiredPlan: 'enterprise', permissionKey: 'pdv_sales.view', moduleKey: 'pdv' },
      { titleKey: 'nav.products', url: '/produtos', icon: Package, requiredPlan: 'enterprise', permissionKey: 'products.view', moduleKey: 'pdv' },
      { titleKey: 'nav.taxInvoices', url: '/notas-fiscais', icon: FileCheck, requiredPlan: 'enterprise', permissionKey: 'nfse.view', moduleKey: 'nfse' },
      { titleKey: 'nav.paymentMethods', url: '/pagamentos', icon: Wallet, requiredPlan: 'enterprise', permissionKey: 'payment_methods.view', moduleKey: 'payment_methods' },
    ],
  },
  {
    labelKey: 'nav.system',
    items: [
      { titleKey: 'nav.staff', url: '/equipe', icon: Users2, permissionKey: 'staff.view', moduleKey: 'staff' },
      { titleKey: 'nav.announcements', url: '/comunicados', icon: Megaphone, permissionKey: 'announcements.view', moduleKey: 'announcements' },
      { titleKey: 'nav.notifications', url: '/notificacoes', icon: BellIcon, permissionKey: 'notifications.view', moduleKey: 'notifications' },
      { titleKey: 'nav.apiDocs', url: '/api-docs', icon: FileText, requiredPlan: 'enterprise', permissionKey: 'api_docs.view', moduleKey: 'api_docs' },
      { titleKey: 'nav.support', url: '/atendimento', icon: LifeBuoy, permissionKey: 'support.view', moduleKey: 'support' },
      { titleKey: 'nav.settings', url: '/configuracoes', icon: Settings, permissionKey: 'settings.view', moduleKey: 'settings' },
    ],
  },
];

/**
 * Navegação do modo professor (PPS para Professores, FASE P0.4). Usada
 * SOMENTE quando `club?.kind === 'professor'` — `navGroups` (clube
 * tradicional) fica intocado. Sem `permissionKey`/`moduleKey`: este modo
 * fica FORA da matriz de permissões (RequirePermission.tsx dá bypass
 * completo em clubes kind='professor' — nav própria, com escopo row-level
 * já garantido por `professor_can_access_*`/`club_teacher_id_for`. Ver
 * invariante do plano de RBAC: "modo professor permanece nav separada de
 * produto, a matriz não é editável nesses espaços").
 */
export const professorNavGroups: NavGroup[] = [
  {
    labelKey: 'professor.nav.overview',
    items: [
      { titleKey: 'professor.nav.home', url: '/', icon: LayoutDashboard },
      { titleKey: 'nav.agenda', url: '/agenda', icon: Calendar },
      { titleKey: 'professor.nav.attendance', url: '/agenda?view=day', icon: UserCheck },
    ],
  },
  {
    labelKey: 'nav.academy',
    items: [
      { titleKey: 'professor.nav.signups', url: '/professor/cadastros', icon: UserPlus },
      { titleKey: 'nav.students', url: '/alunos', icon: GraduationCap },
      { titleKey: 'nav.lessons', url: '/aulas', icon: CalendarDays },
      { titleKey: 'nav.makeup', url: '/reposicoes', icon: RefreshCw },
      { titleKey: 'nav.trialLessons', url: '/experimentais', icon: FlaskConical },
      { titleKey: 'professor.nav.team', url: '/professor/equipe', icon: UserCog },
    ],
  },
  {
    labelKey: 'nav.financial',
    items: [
      { titleKey: 'professor.nav.billing', url: '/professor/financeiro-alunos', icon: Receipt },
      { titleKey: 'nav.financialPanel', url: '/financeiro', icon: BarChart3 },
      { titleKey: 'nav.plans', url: '/planos', icon: CreditCard },
      { titleKey: 'nav.invoices', url: '/faturas', icon: FileText },
      { titleKey: 'nav.commissions', url: '/comissoes', icon: Percent },
    ],
  },
  {
    labelKey: 'professor.nav.more',
    items: [
      { titleKey: 'nav.indicators', url: '/indicadores', icon: BarChart3 },
      { titleKey: 'nav.whatsapp', url: '/whatsapp', icon: WhatsAppIcon },
      { titleKey: 'nav.settings', url: '/configuracoes', icon: Settings },
    ],
  },
];

export const PLAN_LEVEL: Record<string, number> = { free: 0, enterprise: 2, professor: 2 };
